/**
 * Cores da interface escolhidas pelo jogador. Quatro cores base viram variáveis CSS (as demais são
 * derivadas no estilo.css) e ficam guardadas no navegador.
 */

export interface Tema {
  fundo: string;
  lateral: string;
  topo: string;
  janela: string;
}

export const TEMA_PADRAO: Tema = { fundo: '#1b5a2a', lateral: '#1ea84f', topo: '#4d2615', janela: '#0e4a74' };

export const TEMAS: { nome: string; tema: Tema }[] = [
  { nome: 'Clássico', tema: TEMA_PADRAO },
  { nome: 'Noite', tema: { fundo: '#1c2a44', lateral: '#3b6fd4', topo: '#0f1626', janela: '#2b3a67' } },
  { nome: 'Vinho', tema: { fundo: '#5a1f2e', lateral: '#c2415d', topo: '#2e0d16', janela: '#6a2a3c' } },
  { nome: 'Grafite', tema: { fundo: '#2b2f33', lateral: '#7a8791', topo: '#15181a', janela: '#3c4650' } },
  { nome: 'Laranja', tema: { fundo: '#7a3a12', lateral: '#e8821c', topo: '#3a1a08', janela: '#8a4a1c' } },
  { nome: 'Roxo', tema: { fundo: '#3d2a66', lateral: '#8a5fd6', topo: '#1d1233', janela: '#53388f' } },
];

const CHAVE = 'bf-tema-v1';
const VARIAVEIS: Record<keyof Tema, string> = { fundo: '--cor-fundo', lateral: '--cor-lateral', topo: '--cor-topo', janela: '--cor-janela' };
const HEX = /^#[0-9a-f]{6}$/i;

let atual: Tema = { ...TEMA_PADRAO };

function aplicar(): void {
  for (const k of Object.keys(VARIAVEIS) as (keyof Tema)[]) {
    document.documentElement.style.setProperty(VARIAVEIS[k], atual[k]);
  }
}

function guardar(): void {
  try {
    localStorage.setItem(CHAVE, JSON.stringify(atual));
  } catch {
    /* armazenamento bloqueado: o tema vale só nesta sessão */
  }
}

export function temaAtual(): Tema {
  return { ...atual };
}

export function carregarTema(): void {
  try {
    const bruto = localStorage.getItem(CHAVE);
    if (bruto) {
      const t = JSON.parse(bruto) as Partial<Tema>;
      for (const k of Object.keys(VARIAVEIS) as (keyof Tema)[]) {
        const v = t[k];
        if (typeof v === 'string' && HEX.test(v)) atual[k] = v;
      }
    }
  } catch {
    atual = { ...TEMA_PADRAO };
  }
  aplicar();
}

export function mudarCor(chave: keyof Tema, cor: string): void {
  if (!(chave in VARIAVEIS) || !HEX.test(cor)) return;
  atual[chave] = cor;
  aplicar();
  guardar();
}

export function escolherTema(tema: Tema): void {
  atual = { ...tema };
  aplicar();
  guardar();
}

const ROTULOS: [keyof Tema, string][] = [
  ['fundo', 'Fundo do jogo'],
  ['lateral', 'Painel lateral'],
  ['topo', 'Barra superior'],
  ['janela', 'Janelas e cabeçalhos'],
];

/** Bloco "Aparência" da janela Opções. */
export function secaoAparencia(): string {
  const t = atual;
  const predefinidos = TEMAS.map(
    (p, i) =>
      `<button type="button" class="btn" data-tema-pre="${i}"><span class="tema-bola"><i style="background:${p.tema.fundo}"></i><i style="background:${p.tema.lateral}"></i><i style="background:${p.tema.topo}"></i><i style="background:${p.tema.janela}"></i></span>${p.nome}</button>`,
  ).join('');
  const linhas = ROTULOS.map(
    ([k, nome]) => `<label class="cor-linha"><input type="color" data-tema-cor="${k}" value="${t[k]}" /> ${nome}</label>`,
  ).join('');
  return `<div class="aparencia"><h3 class="fase-tit">Aparência</h3><div class="temas">${predefinidos}</div>${linhas}</div>`;
}

/** Liga os controles da seção (delegação no documento: sobrevive aos redesenhos). */
export function iniciarTema(): void {
  carregarTema();
  document.addEventListener('input', (ev) => {
    const el = ev.target as HTMLInputElement;
    const k = el?.dataset?.temaCor as keyof Tema | undefined;
    if (k) mudarCor(k, el.value);
  });
  document.addEventListener('click', (ev) => {
    const b = (ev.target as Element).closest<HTMLElement>('[data-tema-pre]');
    if (!b) return;
    const predefinido = TEMAS[Number(b.dataset.temaPre)];
    if (!predefinido) return;
    escolherTema(predefinido.tema);
    for (const campo of document.querySelectorAll<HTMLInputElement>('[data-tema-cor]')) {
      campo.value = atual[campo.dataset.temaCor as keyof Tema];
    }
  });
}
